import { Modal, Form, Rate, Checkbox, Input, Button, Typography, Space, App, Flex, Divider } from "antd";
import { useEffect, useState } from "react";
import { userActions } from "../../actions/user";

const { Text, Title } = Typography;
const { TextArea } = Input;

export default function PlatformFeedback({
  open,
  title = (
    <>
      <Flex vertical>
        <Text className='font-size-20px fw-500'>You have eyes we trust.</Text>
        <Text type='secondary' className='font-size-12px'>
          What's one thing here that could be clearer or feel more elegant?
        </Text>
      </Flex>
      <Divider className='mt-5px mb-10px' style={{ borderColor: "#928888" }} />
    </>
  ),
  product = "",
  eventType = "",
  onClose = () => {},
}) {
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const initialValues = {
    rating: null,
    feedback_text: null,
    wants_contact: false,
  };

  useEffect(() => {
    if (open && product)
      userActions
        .checkFeedbackPrompt(product)
        .then((res) => {
          if (res.shouldPrompt === true) setShowModal(true);
          else {
            setShowModal(false);
            onClose();
          }
        })
        .catch(() => {
          setShowModal(false);
          onClose();
        });
  }, [product, open, onClose]);

  const submitFeedbackForm = (params, showMsg = false) => {
    setSubmitting(true);
    userActions
      .submitFeedback(params)
      .then(() => {
        if (showMsg) notification.success({ title: "", description: "Thank you for your feedback!" });
        form.resetFields();
      })
      .catch(() => {})
      .finally(() => {
        setShowModal(false);
        setSubmitting(false);
        onClose();
      });
  };

  const handleOk = async () => {
    if (submitting || !eventType) return false;

    try {
      const values = await form.validateFields();
      const req = { ...values, product, event_type: eventType, dismissed: 0 };
      submitFeedbackForm(req, true);
    } catch (_) {}
  };

  const handleCancel = async () => {
    if (submitting || !eventType) return false;

    const req = { product, event_type: eventType, dismissed: 1 };
    submitFeedbackForm(req, false);
  };

  const stars = 5;
  const tooltips = Array.from({ length: stars }, (_, i) => `${i + 1}`);
  const modalWidth = 370;

  return (
    <Modal
      open={showModal}
      centered={false}
      transitionName=''
      maskTransitionName=''
      mask={{ closable: false }}
      keyboard={false}
      closable={false}
      onCancel={handleCancel}
      width={modalWidth}
      style={{ margin: 0 }}
      rootClassName='pf-feedback'
      getContainer={false}
      modalRender={(node) => (
        <div
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            width: `min(${modalWidth}px, calc(100vw - 24px))`,
            zIndex: 9000,
          }}
        >
          {node}
        </div>
      )}
      title={
        <Title level={4} style={{ margin: 0 }}>
          {title}
        </Title>
      }
      footer={
        <Space>
          <Button onClick={handleCancel} disabled={submitting}>
            Close
          </Button>
          <Button type='primary' onClick={handleOk} loading={submitting}>
            Send
          </Button>
        </Space>
      }
      destroyOnHidden
    >
      <Form form={form} layout='vertical' initialValues={initialValues}>
        <Form.Item
          name='rating'
          className='mb-12px'
          label={
            <Flex vertical>
              <Text strong>How would you rate us overall?</Text>
              <Text type='secondary font-size-12px'>(1-{stars}, optional)</Text>
            </Flex>
          }
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (value == null) return Promise.resolve();
                if (typeof value === "number" && value >= 1 && value <= stars) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(`Please choose a value between 1 and ${stars}.`));
              },
            }),
          ]}
          validateFirst
        >
          <Rate count={stars} tooltips={tooltips} allowClear />
        </Form.Item>

        <Form.Item
          name='feedback_text'
          label={
            <Space size={8}>
              <Text strong>Tell us more</Text>
            </Space>
          }
          rules={[
            { required: true, whitespace: true, message: "Please enter your feedback." },
            { max: 1000, message: "Feedback must be 1000 characters or fewer." },
          ]}
        >
          <TextArea placeholder='It would be better if…' showCount maxLength={1000} allowClear autoSize={{ minRows: 4, maxRows: 8 }} />
        </Form.Item>

        <Form.Item name='wants_contact' valuePropName='checked'>
          <Checkbox className='font-size-12px'>I'm open to being contacted for deeper context.</Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
}
